import type { InputHTMLAttributes } from 'react';
import { forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import styles from './SearchBar.module.css';

interface SearchBarProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onClear?: () => void;
}

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  ({ value, onClear, className = '', ...props }, ref) => {
    const { t } = useTranslation();
    const hasValue = Boolean(value);
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    const handleContainerClick = () => {
      inputRef.current?.focus();
    };

    return (
      <div 
        className={`${styles.container} ${className}`}
        onClick={handleContainerClick}
      >
        <Search className={styles.icon} size={20} aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          className={styles.input}
          value={value}
          placeholder={t('search.placeholder')}
          {...props}
        />
        {hasValue && onClear && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>
    );
  }
);

SearchBar.displayName = 'SearchBar';
